// -----------------------------------------------------------------------------
// marksheetPrintService.tsx
//
// Builds the HCI marksheet as a standalone HTML document and prints it via a
// popup window. Layout/markup is adapted 1:1 from the marksheet template,
// with the hand-drawn SVG seals and corner flourish swapped for the real
// image files.
//
// The logo files live right next to this file (MarksRecorder/parts/), so
// they're pulled in as bundler asset imports — CRA/webpack resolves each of
// these to a built asset URL at compile time, which is why we can drop them
// straight into the HTML string as-is.
// -----------------------------------------------------------------------------

import hciLogo from './hci-logo.png';
import isoSeal from './iso.png';
import aaiobarSeal from './aaiobar.png';
import smcsSeal from './iso-smcs.png';
import msmeSeal from './msme.png';
import cornerShape from './shape.png';

const LOGOS = {
  hci: hciLogo,
  iso: isoSeal,
  aaiobar: aaiobarSeal,
  smcs: smcsSeal,
  msme: msmeSeal,
  corner: cornerShape,
};

export interface MarksheetSubjectRow {
  subjectName: string;
  maxTheory: number;
  maxLab: number;
  obtainedTheory: number;
  obtainedLab: number;
}

export interface MarksheetData {
  studentName: string;
  fatherName: string;
  courseName: string;
  enrollmentNo: string;
  certificateNo: string;
  batchSession: string;
  issueDate: string;
  photoUrl?: string;
  subjects: MarksheetSubjectRow[];
  /** Defaults to https://www.hciindia.in/student-verification?cert=<certificateNo> if omitted. */
  verificationUrl?: string;
}

const PERFORMANCE_BANDS = [
  { min: 90, label: 'Outstanding', grade: 'AA' },
  { min: 80, label: 'Excellent', grade: 'A+' },
  { min: 70, label: 'Very Good', grade: 'A' },
  { min: 60, label: 'Good', grade: 'B' },
  { min: 50, label: 'Satisfactory', grade: 'C' },
  { min: 40, label: 'Marginal', grade: 'D' },
  { min: 0, label: 'Fail', grade: 'X' },
];

function gradeFor(pct: number) {
  return PERFORMANCE_BANDS.find((b) => pct >= b.min) ?? PERFORMANCE_BANDS[PERFORMANCE_BANDS.length - 1];
}

function computeTotals(subjects: MarksheetSubjectRow[]) {
  const maxTotal = subjects.reduce((s, r) => s + r.maxTheory + r.maxLab, 0);
  const obtainedTotal = subjects.reduce((s, r) => s + r.obtainedTheory + r.obtainedLab, 0);
  const pct = maxTotal > 0 ? (obtainedTotal / maxTotal) * 100 : 0;
  return { maxTotal, obtainedTotal, pct: Math.round(pct * 100) / 100 };
}

function escapeHtml(str: string) {
  const map: Record<string, string> = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
  return str.replace(/[&<>"']/g, (c) => map[c]);
}

function subjectRowsHtml(subjects: MarksheetSubjectRow[]) {
  return subjects
    .map((s) => {
      const maxTotal = s.maxTheory + s.maxLab;
      const obtainedTotal = s.obtainedTheory + s.obtainedLab;
      return `
        <tr>
          <td class="subject-name">${escapeHtml(s.subjectName.toUpperCase())}</td>
          <td>${s.maxTheory}</td><td>${s.maxLab}</td><td>${maxTotal}</td>
          <td>${s.obtainedTheory}</td><td>${s.obtainedLab}</td><td>${obtainedTotal}</td>
        </tr>`;
    })
    .join('');
}

export function generateMarksheetHTML(data: MarksheetData, qrCodeDataUrl: string): string {
  const { maxTotal, obtainedTotal, pct } = computeTotals(data.subjects);
  const { label: performanceLabel, grade } = gradeFor(pct);
  const verificationUrl =
    data.verificationUrl ?? `https://www.hciindia.in/student-verification/`;

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<title>Marksheet - ${escapeHtml(data.studentName)}</title>
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  html, body { height: 100%; }
  body {
    background: #eef1f6;
    font-family: 'Arial', 'Helvetica', sans-serif;
    min-height: 100vh;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 30px;
  }
  .marksheet {
    width: 900px;
    max-width: 100%;
    background: #ffffff;
    position: relative;
    padding: 10px;
    border: 3px solid #1a2a6b;
    margin: auto;
  }
  .inner-border { border: 1px solid #1a2a6b; padding: 26px 34px 22px; position: relative; }

  /* shape.png's knot sits at the top with its vine trailing down the RIGHT
     edge — i.e. it's natively drawn for the top-right corner. Every other
     corner is a mirror of that: .tl flips horizontally, .br flips
     vertically, .bl flips both. (Previously these were swapped, which is
     why every corner showed the wrong mirror variant.) */
  .corner { position: absolute; width: 56px; height: 75px; pointer-events: none; }
  .corner img { width: 100%; height: 100%; object-fit: contain; display: block; }
  .corner.tr { top: 4px; right: 4px; }
  .corner.tl { top: 4px; left: 4px; transform: scaleX(-1); }
  .corner.br { bottom: 4px; right: 4px; transform: scaleY(-1); }
  .corner.bl { bottom: 4px; left: 4px; transform: scale(-1,-1); }

  .header-flex { display: flex; align-items: center; justify-content: center; margin-bottom: 4px; }
  .header-flex img.hci-logo { height: 62px; }

  .legal-block {
    text-align: center; color: #b02a2a; font-weight: 700; font-size: 11.5px;
    line-height: 1.55; margin-bottom: 4px;
  }
  .address-block { text-align: center; color: #1a2a6b; font-size: 11.5px; font-weight: 700; line-height: 1.5; margin-bottom: 10px; }
  .msheet-title {
    text-align: center; font-size: 20px; font-weight: 800; color: #111;
    letter-spacing: 1px; text-decoration: underline; text-underline-offset: 4px; margin-bottom: 14px;
  }

  .info-photo-row { display: flex; justify-content: space-between; align-items: flex-start; gap: 20px; margin-bottom: 16px; }
  .info-list { font-size: 13.5px; color: #111; }
  .info-list .row { display: flex; margin-bottom: 5px; }
  .info-list .label { width: 165px; font-weight: 700; flex-shrink: 0; }
  .info-list .colon { width: 14px; flex-shrink: 0; }

  .photo-col { text-align: right; flex-shrink: 0; margin-left: auto;  justify-items: right; }
  .photo-box { width: 108px; height: 128px; border: 1.5px solid #333; background: #f2f2f2; display: flex; align-items: center; justify-content: center; overflow: hidden; }
  .photo-box img { width: 100%; height: 100%; object-fit: cover; }
  .photo-box .ph-txt { font-size: 10px; color: #888; text-align: center; line-height: 1.4; }
  .issue-date { margin-top: 10px; font-size: 13px; font-weight: 700; color: #111; }

  table.marks-table { width: 100%; border-collapse: collapse; margin-bottom: 14px; font-size: 12px; }
  table.marks-table th, table.marks-table td { border: 1px solid #222; padding: 4px 6px; text-align: center; }
  table.marks-table th { font-weight: 700; background: #fff; }
  table.marks-table td.subject-name { text-align: left; font-weight: 700; }
  table.marks-table tfoot td { font-weight: 700; }

  .bottom-row { display: flex; justify-content: space-between; align-items: center; gap: 20px; margin-bottom: 18px; }
  .seals-block { display: flex; gap: 12px; align-items: center; flex-shrink: 0; }
  .seals-block img { height: 62px; width: auto; object-fit: contain; }

  table.perf-table { border-collapse: collapse; font-size: 8.8px; flex-shrink: 0; }
  table.perf-table th, table.perf-table td { border: 1px solid #222; padding: 3.3px 6.6px; text-align: center; }
  table.perf-table td.perf-name { font-weight: 700; text-align: left; }

  /* Grid instead of flex so the QR is centered on the full page width, not
     just centered in whatever space is left after the signature block. */
  .sig-row { display: grid; grid-template-columns: 1fr 1fr 1fr; align-items: flex-end; margin-bottom: 10px; }
  .sig-block { grid-column: 1; justify-self: start; text-align: center; }
  .sig-line { border-top: 1.5px solid #333; width: 130px; margin: 6px auto 4px; }
  .sig-label { font-size: 12.5px; font-weight: 700; color: #222; }

  .qr-box { grid-column: 2; justify-self: center; text-align: center; }
  .qr-box img { width: 78px; height: 78px; display: block; }

  .footer-note { text-align: center; font-size: 11px; font-style: italic; color: #333; margin-top: 8px; }

  @media print {
    html, body { height: auto; }
    body { background: #fff; padding: 0; min-height: 0; display: block; }
    .marksheet { box-shadow: none; margin: 0 auto; }
  }
</style>
</head>
<body>
  <div class="marksheet">
    <div class="inner-border">
      <div class="corner tl"><img src="${LOGOS.corner}" alt="" /></div>
      <div class="corner tr"><img src="${LOGOS.corner}" alt="" /></div>
      <div class="corner bl"><img src="${LOGOS.corner}" alt="" /></div>
      <div class="corner br"><img src="${LOGOS.corner}" alt="" /></div>

      <div class="header-flex">
        <img class="hci-logo" src="${LOGOS.hci}" alt="Hindustan Computer Institute" />
      </div>

      <div class="legal-block">
        Registered Trademark of Parshuram Skills Academy (OPC) Pvt. Ltd.<br>
        Incorporated Under the Companies Act, 2013 (18 of 2013),<br>
        Ministry of Corporate Affairs, Government of India.<br>
        CIN: U85499JK2023OPC014559<br>
        ISO 9001:2015 Certified
      </div>

      <div class="address-block">
        Billawar Phinter Road Near Sacred Heart Church Dher, Billawar, Kathua, J&amp;K<br>
        www.hciindia.in
      </div>

      <div class="msheet-title">MARKSHEET</div>

      <div class="info-photo-row">
        <div class="info-list">
          <div class="row"><span class="label">Name</span><span class="colon">:</span><span>${escapeHtml(data.studentName.toUpperCase())}</span></div>
          <div class="row"><span class="label">Father's Name</span><span class="colon">:</span><span>${escapeHtml(data.fatherName)}</span></div>
          <div class="row"><span class="label">Course</span><span class="colon">:</span><span>${escapeHtml(data.courseName)}</span></div>
          <div class="row"><span class="label">Enrollment No</span><span class="colon">:</span><span>${escapeHtml(data.enrollmentNo)}</span></div>
          <div class="row"><span class="label">Certificate No</span><span class="colon">:</span><span>${escapeHtml(data.certificateNo)}</span></div>
          <div class="row"><span class="label">Batch / Session</span><span class="colon">:</span><span>${escapeHtml(data.batchSession)}</span></div>
          <div class="row"><span class="label">Marks Obtained</span><span class="colon">:</span><span>${obtainedTotal} out of ${maxTotal}</span></div>
          <div class="row"><span class="label">Percentage Obtained</span><span class="colon">:</span><span>${pct.toFixed(2)} %</span></div>
          <div class="row"><span class="label">Grade</span><span class="colon">:</span><span>${grade}</span></div>
        </div>
        <div class="photo-col">
          <div class="photo-box">
            ${data.photoUrl ? `<img src="${data.photoUrl}" alt="Student photo" />` : `<div class="ph-txt">STUDENT<br>PHOTO</div>`}
          </div>
          <div class="issue-date">Issue Date: ${escapeHtml(data.issueDate)}</div>
        </div>
      </div>

      <table class="marks-table">
        <thead>
          <tr>
            <th rowspan="2" style="text-align:left;">Subject</th>
            <th colspan="3">MAX MARKS</th>
            <th colspan="3">MARKS OBTAINED</th>
          </tr>
          <tr>
            <th>THEORY</th><th>LAB</th><th>TOTAL</th>
            <th>THEORY</th><th>LAB</th><th>TOTAL</th>
          </tr>
        </thead>
        <tbody>${subjectRowsHtml(data.subjects)}</tbody>
        <tfoot>
          <tr><td colspan="4" style="text-align:left;">Total</td><td colspan="3">${obtainedTotal} / ${maxTotal}</td></tr>
        </tfoot>
      </table>

      <div class="bottom-row">
        <div class="seals-block">
          <img src="${LOGOS.iso}" alt="ISO 9001:2015" />
          <img src="${LOGOS.aaiobar}" alt="AIAO-BAR Accredited" />
          <img src="${LOGOS.smcs}" alt="SMCS Certification" />
          <img src="${LOGOS.msme}" alt="MSME Registered" />
        </div>

        <table class="perf-table">
          <thead><tr><th>Performance</th><th>Percentage Range</th><th>Grade</th></tr></thead>
          <tbody>
            <tr><td class="perf-name">Outstanding</td><td>90% and above</td><td>AA</td></tr>
            <tr><td class="perf-name">Excellent</td><td>80% - 89%</td><td>A+</td></tr>
            <tr><td class="perf-name">Very Good</td><td>70% - 79%</td><td>A</td></tr>
            <tr><td class="perf-name">Good</td><td>60% - 69%</td><td>B</td></tr>
            <tr><td class="perf-name">Satisfactory</td><td>50% - 59%</td><td>C</td></tr>
            <tr><td class="perf-name">Marginal</td><td>40% - 49%</td><td>D</td></tr>
            <tr><td class="perf-name">Fail</td><td>Below 40%</td><td>X</td></tr>
          </tbody>
        </table>
      </div>

      <div class="sig-row">
        <div class="sig-block">
          <div class="sig-line"></div>
          <div class="sig-label">Director</div>
        </div>
        <div class="qr-box">
          <img src="${qrCodeDataUrl}" alt="Scan to verify" />
        </div>
      </div>

      <div class="footer-note">Note: To verify Scan this QR Code or Visit ${escapeHtml(verificationUrl)}</div>
    </div>
  </div>
</body>
</html>`;
}

/**
 * Opens a popup window with the rendered marksheet and triggers the browser
 * print dialog once images have loaded. Requires the `qrcode` package:
 *   npm install qrcode
 *   npm install --save-dev @types/qrcode
 */
export async function printMarksheet(data: MarksheetData): Promise<void> {
  // Open the window synchronously (before the first await) so browsers don't
  // treat it as an unrequested popup and block it.
  const printWindow = window.open('', '_blank', 'width=1000,height=1200');
  if (!printWindow) {
    // eslint-disable-next-line no-alert
    alert('Please allow pop-ups for this site to print the marksheet.');
    return;
  }
  printWindow.document.write('<p style="font-family:sans-serif;padding:20px;">Preparing marksheet…</p>');

  const verificationUrl =
    data.verificationUrl ?? `https://www.hciindia.in/student-verification?cert=${encodeURIComponent}`;

  let qrCodeDataUrl: string;
  try {
    const QRCode = await import('qrcode');
    qrCodeDataUrl = await QRCode.toDataURL(verificationUrl, {
      margin: 0,
      width: 200,
      color: { dark: '#1a1a1a', light: '#ffffff' },
    });
  } catch (err) {
    console.error('Failed to generate verification QR code', err);
    qrCodeDataUrl = ''; // falls back to a broken-image icon rather than blocking print entirely
  }

  const html = generateMarksheetHTML(data, qrCodeDataUrl);
  printWindow.document.open();
  printWindow.document.write(html);
  printWindow.document.close();
  printWindow.onload = () => {
    printWindow.focus();
    printWindow.print();
  };
}

/**
 * Placeholder data so the print flow can be exercised end-to-end before
 * real student/marks data is wired up.
 */
export function getDummyMarksheetData(): MarksheetData {
  return {
    studentName: 'John Doe',
    fatherName: 'Robert Doe',
    courseName: 'Sample Course (Print Preview)',
    enrollmentNo: 'DUMMY0001',
    certificateNo: 'CERT-DUMMY',
    batchSession: 'April 2026 - March 2027',
    issueDate: new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }),
    subjects: [
      { subjectName: 'Sample Subject 1', maxTheory: 60, maxLab: 40, obtainedTheory: 45, obtainedLab: 30 },
      { subjectName: 'Sample Subject 2', maxTheory: 60, maxLab: 40, obtainedTheory: 50, obtainedLab: 35 },
      { subjectName: 'Sample Subject 3', maxTheory: 60, maxLab: 40, obtainedTheory: 42, obtainedLab: 28 },
      { subjectName: 'Sample Subject 4', maxTheory: 60, maxLab: 40, obtainedTheory: 48, obtainedLab: 32 },
      { subjectName: 'Sample Subject 5', maxTheory: 60, maxLab: 40, obtainedTheory: 51, obtainedLab: 33 },
      { subjectName: 'Sample Subject 6', maxTheory: 60, maxLab: 40, obtainedTheory: 44, obtainedLab: 29 },
      { subjectName: 'Sample Subject 7', maxTheory: 60, maxLab: 40, obtainedTheory: 55, obtainedLab: 36 },
    ],
  };
}